import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { ConfigProvider, Spin } from 'antd';
import i18n from './i18n';
import './i18n';

// Pages
import Dashboard from './pages/Dashboard';
import SalesDashboard from './pages/Sales/SalesDashboard';
import LeadDetailsPage from './pages/Sales/LeadDetailsPage';
import Customers from './pages/Sales/Customers';
import Workspaces from './pages/Workspaces/Workspaces';
import Users from './pages/Users';
import PricePlans from './pages/Finance/PricePlans';
import CustomerDetailsPage from './pages/Sales/CustomerDetailsPage';
import Support from './pages/Support';
import WorkspaceDetailsPage from './pages/Workspaces/WorkspaceDetailsPage';
import OpportunitiesPage from './pages/Sales/OpportunitiesPage';
import OpportunityDetailsPage from './pages/Sales/OpportunityDetailsPage';
import NewLeadsPage from './pages/Sales/NewLeadsPage';
import DiscoverPage from './pages/Discover/DiscoverPage';
import AdministratorLeadsPage from './pages/AdministratorLeads/AdministratorLeadsPage';
import UnsubscribePage from './pages/UnsubscribePage';
import { Login } from './pages/Auth';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import PricePlanDetailsPage from './pages/Finance/PricePlanDetailsPage';
import SupportTicketDetailsPage from './pages/Support/SupportTicketDetailsPage';
import UserDetailsPage from './pages/Users/UserDetailsPage';

// Layout
import AdminLayout from './layouts/AdminLayout';

const queryClient = new QueryClient();

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { firestoreUser, loading, firebaseUser } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Only redirect if there's no Firebase user (not logged in)
  // If there's a Firebase user but no Firestore user, we're still loading
  if (!firebaseUser) {
    return <Navigate to="/login" replace />;
  }

  // If we have a Firebase user but no Firestore user yet, show loading
  if (firebaseUser && !firestoreUser) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return <>{children}</>;
};

// Logout handler component
const Logout = () => {
  const { logout } = useAuth();
  React.useEffect(() => {
    logout().finally(() => {
      window.location.replace('/login');
    });
  }, [logout]);
  return null;
};

function App() {
  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        },
      }}
    >
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18n}>
          <AuthProvider>
            <BrowserRouter>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/logout" element={<Logout />} />
                <Route path="/unsubscribe" element={<UnsubscribePage />} />

                {/* Protected routes with AdminLayout as parent */}
                <Route
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <Outlet />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                >
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="discover" element={<DiscoverPage />} />
                  <Route path="sales" element={<SalesDashboard />} />
                  <Route path="sales/leads" element={<NewLeadsPage />} />
                  <Route path="sales/leads/:id" element={<LeadDetailsPage />} />
                  <Route path="sales/administrator-leads" element={<AdministratorLeadsPage />} />
                  <Route path="sales/customers" element={<Customers />} />
                  <Route path="sales/customers/:id" element={<CustomerDetailsPage />} />
                  <Route path="sales/opportunities" element={<OpportunitiesPage />} />
                  <Route path="sales/opportunities/:id" element={<OpportunityDetailsPage />} />
                  <Route path="workspaces" element={<Workspaces />} />
                  <Route path="workspaces/:id" element={<WorkspaceDetailsPage />} />
                  <Route path="users" element={<Users />} />
                  <Route path="finance/billing" element={<CustomerDetailsPage />} />
                  <Route path="finance/price-plans" element={<PricePlans />} />
                  <Route path="finance/price-plans/:id" element={<PricePlanDetailsPage />} />
                  <Route path="support" element={<Support />} />
                  <Route path="support/:id" element={<SupportTicketDetailsPage />} />
                  <Route path="users/:id" element={<UserDetailsPage />} />
                </Route>
              </Routes>
            </BrowserRouter>
          </AuthProvider>
        </I18nextProvider>
      </QueryClientProvider>
    </ConfigProvider>
  );
}

export default App;
